import { useState, useEffect } from 'react';
import { Users, FileText, MessageSquare, Eye, Heart } from 'lucide-react';
import { useAuth } from '../../../contexts';
import { TabbedStatCard, type TabType, type TabbedStatsConfig } from '../../molecules/TabbedStatCard';
import { LatestContentCard } from '../../molecules/LatestContentCard';
import { RecentUsersList } from '../../organisms/RecentUsersList';
import { usersService, contentsService, commentsService, likesService } from '../../../services';
import type { UserStats, RecentUser, ContentStats, LatestContent, HitCountStats, CommentStats, LikeStats } from '../../../services';

const userStatsTabConfig: TabbedStatsConfig = {
  daily: {
    label: 'Bugün',
    gradient: 'bg-gradient-to-br from-green-500 to-emerald-600',
    description: 'Bugün kayıt olan kullanıcı sayısı',
  },
  monthly: {
    label: 'Bu Ay',
    gradient: 'bg-gradient-to-br from-blue-500 to-cyan-600',
    description: 'Bu ay kayıt olan kullanıcı sayısı',
  },
  yearly: {
    label: 'Bu Yıl',
    gradient: 'bg-gradient-to-br from-orange-500 to-red-600',
    description: 'Bu yıl kayıt olan kullanıcı sayısı',
  },
  total: {
    label: 'Toplam',
    gradient: 'bg-gradient-to-br from-purple-500 to-pink-600',
    description: 'Toplam aktif kullanıcı sayısı',
  },
};

const contentStatsTabConfig: TabbedStatsConfig = {
  daily: {
    label: 'Bugün',
    gradient: 'bg-gradient-to-br from-lime-500 to-green-600',
    description: 'Bugün yayınlanan içerik sayısı',
  },
  monthly: {
    label: 'Bu Ay',
    gradient: 'bg-gradient-to-br from-indigo-500 to-violet-600',
    description: 'Bu ay yayınlanan içerik sayısı',
  },
  yearly: {
    label: 'Bu Yıl',
    gradient: 'bg-gradient-to-br from-amber-500 to-yellow-600',
    description: 'Bu yıl yayınlanan içerik sayısı',
  },
  total: {
    label: 'Toplam',
    gradient: 'bg-gradient-to-br from-rose-500 to-fuchsia-600',
    description: 'Toplam yayınlanmış içerik sayısı',
  },
};

const commentStatsTabConfig: TabbedStatsConfig = {
  daily: {
    label: 'Bugün',
    gradient: 'bg-gradient-to-br from-sky-500 to-blue-600',
    description: 'Bugün yapılan yorum sayısı',
  },
  monthly: {
    label: 'Bu Ay',
    gradient: 'bg-gradient-to-br from-teal-500 to-cyan-600',
    description: 'Bu ay yapılan yorum sayısı',
  },
  yearly: {
    label: 'Bu Yıl',
    gradient: 'bg-gradient-to-br from-emerald-500 to-teal-600',
    description: 'Bu yıl yapılan yorum sayısı',
  },
  total: {
    label: 'Toplam',
    gradient: 'bg-gradient-to-br from-blue-500 to-indigo-600',
    description: 'Toplam aktif yorum sayısı',
  },
};

const likeStatsTabConfig: TabbedStatsConfig = {
  daily: {
    label: 'Bugün',
    gradient: 'bg-gradient-to-br from-pink-500 to-rose-600',
    description: 'Bugün yapılan beğeni sayısı',
  },
  monthly: {
    label: 'Bu Ay',
    gradient: 'bg-gradient-to-br from-red-500 to-pink-600',
    description: 'Bu ay yapılan beğeni sayısı',
  },
  yearly: {
    label: 'Bu Yıl',
    gradient: 'bg-gradient-to-br from-fuchsia-500 to-pink-600',
    description: 'Bu yıl yapılan beğeni sayısı',
  },
  total: {
    label: 'Toplam',
    gradient: 'bg-gradient-to-br from-rose-500 to-red-600',
    description: 'Tüm zamanlardaki toplam beğeni sayısı',
  },
};

const hitCountStatsTabConfig: TabbedStatsConfig = {
  daily: {
    label: 'Bugün',
    gradient: 'bg-gradient-to-br from-blue-500 to-cyan-600',
    description: 'Bugün okunan içerik sayısı',
  },
  monthly: {
    label: 'Bu Ay',
    gradient: 'bg-gradient-to-br from-orange-500 to-red-600',
    description: 'Bu ay okunan içerik sayısı',
  },
  yearly: {
    label: 'Bu Yıl',
    gradient: 'bg-gradient-to-br from-green-500 to-emerald-600',
    description: 'Bu yıl okunan içerik sayısı',
  },
  total: {
    label: 'Toplam',
    gradient: 'bg-gradient-to-br from-indigo-500 to-violet-600',
    description: 'Tüm zamanların toplam okunma sayısı',
  },
};

export const DashboardPage = () => {
  const { user } = useAuth();
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [userStatsLoading, setUserStatsLoading] = useState(true);
  const [userStatsError, setUserStatsError] = useState<string | null>(null);

  const [contentStats, setContentStats] = useState<ContentStats | null>(null);
  const [contentStatsLoading, setContentStatsLoading] = useState(true);
  const [contentStatsError, setContentStatsError] = useState<string | null>(null);

  const [commentStats, setCommentStats] = useState<CommentStats | null>(null);
  const [commentStatsLoading, setCommentStatsLoading] = useState(true);
  const [commentStatsError, setCommentStatsError] = useState<string | null>(null);

  const [likeStats, setLikeStats] = useState<LikeStats | null>(null);
  const [likeStatsLoading, setLikeStatsLoading] = useState(true);
  const [likeStatsError, setLikeStatsError] = useState<string | null>(null);

  const [latestContents, setLatestContents] = useState<LatestContent[]>([]);
  const [latestContentsLoading, setLatestContentsLoading] = useState(true);
  const [latestContentsError, setLatestContentsError] = useState<string | null>(null);

  const [hitCountStats, setHitCountStats] = useState<HitCountStats | null>(null);
  const [hitCountStatsLoading, setHitCountStatsLoading] = useState(true);
  const [hitCountStatsError, setHitCountStatsError] = useState<string | null>(null);

  const [recentUsersPeriod, setRecentUsersPeriod] = useState<1 | 7>(1);
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([]);
  const [recentUsersLoading, setRecentUsersLoading] = useState(true);
  const [recentUsersError, setRecentUsersError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserStats = async () => {
      try {
        setUserStatsLoading(true);
        const stats = await usersService.getStats();
        setUserStats(stats);
        setUserStatsError(null);
      } catch (err) {
        setUserStatsError(err instanceof Error ? err.message : 'Kullanıcı istatistikleri yüklenirken bir hata oluştu');
        console.error('Failed to fetch user stats:', err);
      } finally {
        setUserStatsLoading(false);
      }
    };

    const fetchContentStats = async () => {
      try {
        setContentStatsLoading(true);
        const stats = await contentsService.getStats();
        setContentStats(stats);
        setContentStatsError(null);
      } catch (err) {
        setContentStatsError(err instanceof Error ? err.message : 'İçerik istatistikleri yüklenirken bir hata oluştu');
        console.error('Failed to fetch content stats:', err);
      } finally {
        setContentStatsLoading(false);
      }
    };

    const fetchCommentStats = async () => {
      try {
        setCommentStatsLoading(true);
        const stats = await commentsService.getStats();
        setCommentStats(stats);
        setCommentStatsError(null);
      } catch (err) {
        setCommentStatsError(err instanceof Error ? err.message : 'Yorum istatistikleri yüklenirken bir hata oluştu');
        console.error('Failed to fetch comment stats:', err);
      } finally {
        setCommentStatsLoading(false);
      }
    };

    const fetchLatestContents = async () => {
      try {
        setLatestContentsLoading(true);
        const contents = await contentsService.getLatest(3);
        setLatestContents(contents);
        setLatestContentsError(null);
      } catch (err) {
        setLatestContentsError(err instanceof Error ? err.message : 'Son gönderiler yüklenirken bir hata oluştu');
        console.error('Failed to fetch latest contents:', err);
      } finally {
        setLatestContentsLoading(false);
      }
    };

    const fetchHitCountStats = async () => {
      try {
        setHitCountStatsLoading(true);
        const stats = await contentsService.getHitCountStats();
        setHitCountStats(stats);
        setHitCountStatsError(null);
      } catch (err) {
        setHitCountStatsError(
          err instanceof Error
            ? err.message
            : 'Okunma istatistikleri yüklenirken bir hata oluştu',
        );
        console.error('Failed to fetch hitcount stats:', err);
      } finally {
        setHitCountStatsLoading(false);
      }
    };

    const fetchLikeStats = async () => {
      try {
        setLikeStatsLoading(true);
        const stats = await likesService.getStats();
        setLikeStats(stats);
        setLikeStatsError(null);
      } catch (err) {
        setLikeStatsError(err instanceof Error ? err.message : 'Beğeni istatistikleri yüklenirken bir hata oluştu');
        console.error('Failed to fetch like stats:', err);
      } finally {
        setLikeStatsLoading(false);
      }
    };

    const fetchRecentUsers = async (days: number) => {
      try {
        setRecentUsersLoading(true);
        const users = await usersService.getRecentUsers(days);
        setRecentUsers(users);
        setRecentUsersError(null);
      } catch (err) {
        setRecentUsersError(
          err instanceof Error
            ? err.message
            : `Son ${days} gün üye olanlar yüklenirken bir hata oluştu`,
        );
        console.error(`Failed to fetch recent users (${days} days):`, err);
      } finally {
        setRecentUsersLoading(false);
      }
    };

    fetchUserStats();
    fetchContentStats();
    fetchCommentStats();
    fetchLikeStats();
    fetchLatestContents();
    fetchHitCountStats();
    fetchRecentUsers(1);
  }, []);

  useEffect(() => {
    const fetchRecentUsers = async (days: number) => {
      try {
        setRecentUsersLoading(true);
        const users = await usersService.getRecentUsers(days);
        setRecentUsers(users);
        setRecentUsersError(null);
      } catch (err) {
        setRecentUsersError(
          err instanceof Error
            ? err.message
            : `Son ${days} gün üye olanlar yüklenirken bir hata oluştu`,
        );
        console.error(`Failed to fetch recent users (${days} days):`, err);
      } finally {
        setRecentUsersLoading(false);
      }
    };

    fetchRecentUsers(recentUsersPeriod);
  }, [recentUsersPeriod]);

  const getUserStatsValue = (tab: TabType, stats: UserStats): number => {
    switch (tab) {
      case 'daily':
        return stats.daily;
      case 'monthly':
        return stats.monthly;
      case 'yearly':
        return stats.yearly;
      case 'total':
        return stats.totalActive;
      default:
        return 0;
    }
  };

  const getContentStatsValue = (tab: TabType, stats: ContentStats): number => {
    switch (tab) {
      case 'daily':
        return stats.daily;
      case 'monthly':
        return stats.monthly;
      case 'yearly':
        return stats.yearly;
      case 'total':
        return stats.totalPublished;
      default:
        return 0;
    }
  };

  const getCommentStatsValue = (tab: TabType, stats: CommentStats): number => {
    switch (tab) {
      case 'daily':
        return stats.daily;
      case 'monthly':
        return stats.monthly;
      case 'yearly':
        return stats.yearly;
      case 'total':
        return stats.totalActive;
      default:
        return 0;
    }
  };

  const getHitCountStatsValue = (tab: TabType, stats: HitCountStats): number => {
    switch (tab) {
      case 'daily':
        return stats.daily;
      case 'monthly':
        return stats.monthly;
      case 'yearly':
        return stats.yearly;
      case 'total':
        return stats.total;
      default:
        return 0;
    }
  };

  const getLikeStatsValue = (tab: TabType, stats: LikeStats): number => {
    switch (tab) {
      case 'daily':
        return stats.daily;
      case 'monthly':
        return stats.monthly;
      case 'yearly':
        return stats.yearly;
      case 'total':
        return stats.total;
      default:
        return 0;
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-gray-50 to-white min-h-screen">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
            Dashboard
          </h1>
        </div>
        <p className="text-gray-600 text-base sm:text-lg">
          Hoş geldiniz, <span className="font-semibold text-accent">{user?.username}</span>
        </p>
      </div>

      {/* Statistics with Tabs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <TabbedStatCard
          title="Kullanıcı İstatistikleri"
          icon={Users}
          stats={userStats}
          loading={userStatsLoading}
          error={userStatsError}
          tabConfig={userStatsTabConfig}
          getValue={getUserStatsValue}
        />
        <TabbedStatCard
          title="İçerik İstatistikleri"
          icon={FileText}
          stats={contentStats}
          loading={contentStatsLoading}
          error={contentStatsError}
          tabConfig={contentStatsTabConfig}
          getValue={getContentStatsValue}
        />
        <TabbedStatCard
          title="Yorum İstatistikleri"
          icon={MessageSquare}
          stats={commentStats}
          loading={commentStatsLoading}
          error={commentStatsError}
          tabConfig={commentStatsTabConfig}
          getValue={getCommentStatsValue}
        />
        <TabbedStatCard
          title="Okunma İstatistikleri"
          icon={Eye}
          stats={hitCountStats}
          loading={hitCountStatsLoading}
          error={hitCountStatsError}
          tabConfig={hitCountStatsTabConfig}
          getValue={getHitCountStatsValue}
        />
        <TabbedStatCard
          title="Beğeni İstatistikleri"
          icon={Heart}
          stats={likeStats}
          loading={likeStatsLoading}
          error={likeStatsError}
          tabConfig={likeStatsTabConfig}
          getValue={getLikeStatsValue}
        />
      </div>

      {/* Latest Posts */}
      <div className="mb-8">
        {latestContentsLoading ? (
          <div className="text-center text-gray-500 py-8">Gönderiler yükleniyor...</div>
        ) : latestContentsError ? (
          <div className="text-center text-red-500 py-8">{latestContentsError}</div>
        ) : latestContents.length === 0 ? (
          <div className="text-center text-gray-500 py-8">Henüz gönderi bulunmuyor</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {latestContents.map((content) => (
              <LatestContentCard key={content.id} content={content} />
            ))}
          </div>
        )}
      </div>

      {/* Recent Users */}
      <div className="mb-8">
        <RecentUsersList
          users={recentUsers}
          period={recentUsersPeriod}
          onPeriodChange={setRecentUsersPeriod}
          loading={recentUsersLoading}
          error={recentUsersError}
        />
      </div>

    </div>
  );
};

